/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import Extent from '@arcgis/core/geometry/Extent';
import Point from '@arcgis/core/geometry/Point';
import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import debounce from 'lodash.debounce';
import isEmpty from 'lodash.isempty';
import isNil from 'lodash.isnil';
import throttle from 'lodash.throttle';
import {useRouter} from 'next/compat/router';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {CVSAssetType} from '@ion/core/src/types/entities';
import {ColorModeEnum} from '@ion/dls/src/types/types';
import useThemeContext from '../../theme/useThemeContext';
import {
  ALL_FEATURES_DEF,
  FLEET_ASSET_LAYER_FIELDS,
  FLEET_ASSET_LAYER_ID,
  FLEET_SELECTED_ASSET_LAYER_ID,
  FleetLayerProps,
  getClusteringRenderer,
  IDENTIFIER_PROPERTY,
  IDENTIFIER_TYPE,
  MAPVIEW_EXTENT_THRESHOLD,
  NONE_FEATURES_DEF,
  WEBMAP_CVS_TITLE
} from '../constants';
import {getUnappliedItems, queueLayerItems} from '../helpers/layerEditQueue';
import {
  addGraphicsToLayer,
  createGraphics,
  highlightGraphic
} from '../helpers/LayerUtils';
import {
  applyDynamicLatLngStyling,
  applyEditsToLayer,
  createClientAssetRenderer,
  getLabelClass,
  getLayerByTitle,
  layerExists,
  viewHitTest
} from '../helpers/MapViewUtils';
import {useAnimationAssetToPosition} from '../hooks/useAnimationAssetToPosition';
import {useFeatureLayerEffect} from '../hooks/useAssetFeatureLayerEffect';
import {useAssetLayerAnimation} from '../hooks/useAssetLayerAnimation';
import usePanToAsset from '../hooks/usePanToAsset';
import {isCurrentEnabled} from './LayerChooser';

/**
 * Defines the FleetAssetLayer which manages rendering a layer containing individual and
 * clusters graphics.
 *
 * ```typescript
 *
 *   import FleetLayer from '@ion/dls/src/maps/layers/FleetAssetLayer';
 * 
 *   <FleetAssetLayer props={...props} />
 *
 * ```
 */
const FleetAssetLayer = ({
  map,
  view,
  layerProps : {
    assets,
    layers,
    basemap,
    onShowPopup,
    routeUpdates,
    assetUpdates,
    labelContent,
    viewZoomLevel,
    globalGroupId,
    featureFilter,
    selectedAsset,
    setAssetState,
    isMapSelection,
    setLayerEnabled,
    isLabellingActive,
    isClusteringActive,
    findNearbyIsActive,
    setFleetAssetReady,
    onFindNearbyRefresh,
    onZoomToClusterHandler,
    setFleetAssetLayerRendered,
    isSmoothAssetMovementActive = false
  } = {} as any,
  clusterColor = ColorModeEnum.dark
}: FleetLayerProps, ref) => {
  const isMounted                         = useHasMounted(null);
  const route                             = useRouter();
  const fleetAssetLayerRef                = useRef<FeatureLayer | null>(null);
  const fleetSelectedAssetLayerRef        = useRef<FeatureLayer | null>(null);
  const fleetAssetLayerViewRef            = useRef(null);
  const previousGlobalGroupIdRef          = useRef(null);
  const viewClickRef                      = useRef(null);
  const viewRef                           = useRef(view);
  const activeLayersRef                   = useRef(layers);
  const selectedAssetRef                  = useRef(selectedAsset);
  const smoothSelectedAssetRef            = useRef(null);
  const isMapSelectionRef                 = useRef(isMapSelection);
  const isSmoothAssetMovementActiveRef    = useRef(isSmoothAssetMovementActive);
  const viewZoomLevelRef                  = useRef(viewZoomLevel);
  const appliedAssetsRef                  = useRef(new WeakSet<object>());
  const pendingAssetsRef                  = useRef<CVSAssetType[]>([]);
  const cvsLayer                          = getLayerByTitle(map?.layers as any, WEBMAP_CVS_TITLE);
  const isCVSRenderer                     = !isNil(cvsLayer?.renderer);
  const panToAsset                        = usePanToAsset(view, selectedAsset);
  const [layerRendered, setLayerRendered] = useState(false);

  const isTrackingBetaActive = useMemo(() => route?.route === '/tracking2', [route?.route]);

  const {
    createSelectionEffect,
    setLayerHighlighter,
    removeHighlight,
    highlightItem
  } = useFeatureLayerEffect();

  useImperativeHandle(ref, () => ({getAssetExtent}));

  const highlightFeature = useCallback(() => {
    highlightGraphic({
      layer         : fleetSelectedAssetLayerRef.current,
      selectedAsset : selectedAssetRef.current,
      createSelectionEffect,
      removeHighlight,
      highlightItem
    });
  }, []);

  const panMapToSelectedAsset = useCallback((selectedAsset: CVSAssetType, closePopup: boolean = false) => {
    if (selectedAsset) {
      const {extent} = viewRef.current || {};
      (!isMapSelectionRef.current || closePopup) && view.closePopup();
      const graphic       = createGraphics([selectedAsset], FLEET_ASSET_LAYER_FIELDS);
      const extentReduced = extent.clone().expand(MAPVIEW_EXTENT_THRESHOLD);
      if (!extentReduced.contains(graphic[0].geometry)) {
        panToAsset(selectedAsset);
      }
    }
  }, [panToAsset, createGraphics]);

  const {isCurrent: isCurrentTheme} = useThemeContext();

  const animateLayerProps = {
    // current theme: every moving asset's frame goes out in one batched edit ...
    batchEdits : isCurrentTheme,
    isSmoothAssetMovementActiveRef,
    fleetSelectedAssetLayerRef,
    panMapToSelectedAsset,
    onFindNearbyRefresh,
    fleetAssetLayerRef,
    selectedAssetRef,
    highlightFeature,
    view
  };

  const {
    animateAssetOnMapLayers,
    lastApplyEditTimeRef,
    resetObjectIdCache,
    objectIdCacheRef,
    editQueueRef
  } = useAssetLayerAnimation(animateLayerProps);

  const {
    animateAssetPosition,
    stopAllAssetAnimation,
    stopAssetAnimation
  } = useAnimationAssetToPosition(animateAssetOnMapLayers as any);

  const onFleetAssetLayerRendered = useCallback(() => {
    setLayerRendered(true);
    setFleetAssetLayerRendered(true);
  }, []);

  const setDefinitionExpressionToFleetAssetLayer = useCallback(() => {
    if (fleetAssetLayerRef.current) {
      if (selectedAssetRef.current) {
        fleetAssetLayerRef.current.definitionExpression = `assetId <> ${selectedAssetRef.current.assetId}`;
      } else {
        fleetAssetLayerRef.current.definitionExpression = ALL_FEATURES_DEF;
      }
    }
  }, []);

  const setDefinitionExpressionToFleetSelectedAssetLayer = useCallback(() => {
    if (fleetSelectedAssetLayerRef.current) {
      if (selectedAssetRef.current) {
        fleetSelectedAssetLayerRef.current.definitionExpression = ALL_FEATURES_DEF;
      } else {
        fleetSelectedAssetLayerRef.current.definitionExpression = NONE_FEATURES_DEF;
      }
      setTimeout(() => {
        fleetSelectedAssetLayerRef.current.refresh();
      }, 300);
    }
  }, []);

  const setDefinitionExpressionToLayer = useCallback(() => {
    setDefinitionExpressionToFleetAssetLayer();
    setDefinitionExpressionToFleetSelectedAssetLayer();
  }, []);

  const createFeatureLayer = useCallback((LayerId: string, graphics: __esri.Graphic[], clusterEnabled: boolean) => {
    const renderer         = applyDynamicLatLngStyling(cvsLayer?.renderer || createClientAssetRenderer(), clusterColor);
    const clusteringActive = clusterEnabled && !findNearbyIsActive;
    return new FeatureLayer({
      title            : LayerId,
      id               : LayerId,
      fields           : FLEET_ASSET_LAYER_FIELDS as any,
      featureReduction : clusteringActive ? getClusteringConfig(isClusteringActive, clusterColor) as any : null,
      objectIdField    : 'OBJECTID',
      geometryType     : 'point',
      spatialReference : {wkid: 4326},
      outFields        : ['*'],
      source           : graphics,
      labelingInfo     : getLabelClass({
        selectedAsset      : selectedAssetRef.current,
        identifierType     : IDENTIFIER_TYPE,
        identifierProperty : IDENTIFIER_PROPERTY,
        defaultColor       : true,
        labelContent,
        isCVSRenderer,
        basemap
      }) as any,
      popupEnabled : false,
      renderer
    });
  }, [
    map,
    basemap,
    cvsLayer,
    clusterColor,
    featureFilter,
    labelContent,
    isCVSRenderer,
    findNearbyIsActive,
    isClusteringActive
  ]);
  
  const getAssetExtent = useCallback(async() => {
    const layer = fleetAssetLayerRef.current;
    if (!layer) return null;

    const where = featureFilter?.where?.trim() || ALL_FEATURES_DEF;

    const {features} = await layer.queryFeatures({
      where,
      returnGeometry : true,
      outFields      : []
    });

    if (!features.length) return null;

    if (features.length === 1) {
      const geometry = features[0].geometry;

      if (geometry.type === 'point') {
        const point = geometry as Point;

        return new Extent({
          xmin             : point.x - 0.001,
          ymin             : point.y - 0.001,
          xmax             : point.x + 0.001,
          ymax             : point.y + 0.001,
          spatialReference : point.spatialReference
        });
      }

      if (geometry.extent) {
        return geometry.extent.expand(4);
      }

      return null;
    }

    const {extent} = await layer.queryExtent({where});
    return extent;
  }, [featureFilter?.where]);
  
  const getClusteringConfig = useCallback((
    isClusteringActive: boolean,
    clusterColor: ColorModeEnum
  ) => {
    return isClusteringActive ? getClusteringRenderer(clusterColor) : null;
  }, []);

  const hydrateAssetState = useCallback(throttle(async(globalGroupId: number) => {
    if (globalGroupId && setAssetState) {
      const attributes = await getAllFeatureAttributes();
      setAssetState(attributes, globalGroupId);
    }
  }, 5000, {trailing: false}), [setAssetState]);

  useEffect(() => {
    if (isSmoothAssetMovementActiveRef.current) {
      const delay = setTimeout(() => {
        fleetAssetLayerRef.current?.refresh();
        clearTimeout(delay);
      }, 300);
    }
  }, [isTrackingBetaActive]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') {
        stopAllAssetAnimation();
      }
    };

    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  useEffect(() => {
    resetObjectIdCache();
    isSmoothAssetMovementActiveRef.current = isSmoothAssetMovementActive;
    if (!isSmoothAssetMovementActive) {
      stopAllAssetAnimation();
      editQueueRef.current           = {};
      lastApplyEditTimeRef.current   = {};
      smoothSelectedAssetRef.current = null;
    } else {
      smoothSelectedAssetRef.current = selectedAssetRef.current;
    }
  }, [isSmoothAssetMovementActive]);

  useEffect(() => {
    viewRef.current = view;
  }, [view]);

  useEffect(() => {
    viewZoomLevelRef.current = viewZoomLevel;
  }, [viewZoomLevel]);

  useEffect(() => {
    isMapSelectionRef.current = isMapSelection;
  }, [isMapSelection]);

  useEffect(() => {
    if (isMounted) {
      setFleetAssetReady?.(true);
    }
  }, [isMounted]);

  useEffect(() => {
    return function() {
      hydrateAssetState(previousGlobalGroupIdRef.current);
      viewClickRef?.current?.remove('click', viewClickHandler);
      removeAllFeatures(fleetAssetLayerRef.current);
      removeAllFeatures(fleetSelectedAssetLayerRef.current);
      removeHighlight(fleetSelectedAssetLayerRef.current);
      setFleetAssetReady?.(false);
    };
  }, []);

  useEffect(() => {
    if (fleetAssetLayerViewRef.current) {
      fleetAssetLayerViewRef.current.filter = featureFilter;
    }
  }, [featureFilter, layerRendered]);

  useEffect(() => {
    activeLayersRef.current = layers;
    if (fleetAssetLayerRef.current) {
      const isEnabled = isCurrentEnabled(layers);
      fleetAssetLayerRef.current.visible         = isEnabled;
      fleetSelectedAssetLayerRef.current.visible = isEnabled;
      setLayerEnabled?.(isEnabled);
    }
  }, [layers, setLayerEnabled]);

  useEffect(() => {
    if (fleetAssetLayerRef.current) {
      fleetAssetLayerRef.current.labelsVisible         = isLabellingActive;
      fleetSelectedAssetLayerRef.current.labelsVisible = isLabellingActive;
    }
  }, [isLabellingActive]);

  useEffect(() => {
    if (fleetAssetLayerRef.current) {
      const isClusterEnabled = isClusteringActive && !findNearbyIsActive;
      fleetAssetLayerRef.current.featureReduction = getClusteringConfig(isClusterEnabled, clusterColor) as any;
      fleetAssetLayerRef.current.renderer = applyDynamicLatLngStyling(cvsLayer?.renderer, clusterColor);
    }
  }, [isClusteringActive, findNearbyIsActive, basemap, clusterColor]);

  useEffect(() => {
    selectedAssetRef.current = selectedAsset;
    if (selectedAsset) {
      const isNewSelection =
        !smoothSelectedAssetRef.current ||
        smoothSelectedAssetRef.current?.assetId !== selectedAsset?.assetId;
      if (isNewSelection) {
        addUpdateSelectedAsset([selectedAsset]);
      }
    } else {
      removeAllFeatures(fleetSelectedAssetLayerRef.current);
      objectIdCacheRef.current.selected = {};
      highlightFeature();
    }
    if (isSmoothAssetMovementActiveRef.current) {
      smoothSelectedAssetRef.current = selectedAsset;
    }
    setDefinitionExpressionToLayer();
  }, [selectedAsset]);

  useEffect(() => {
    if (fleetAssetLayerRef.current) {
      const labelClass = getLabelClass({
        selectedAsset      : selectedAsset,
        identifierType     : IDENTIFIER_TYPE,
        identifierProperty : IDENTIFIER_PROPERTY,
        defaultColor       : true,
        labelContent,
        isCVSRenderer,
        basemap
      });
      fleetAssetLayerRef.current.labelingInfo         = labelClass;
      fleetSelectedAssetLayerRef.current.labelingInfo = labelClass;
    }
  }, [selectedAsset, labelContent, basemap]);

  useEffect(() => {
    if (layerRendered && selectedAsset && !isSmoothAssetMovementActiveRef.current) {
      panMapToSelectedAsset(selectedAsset);
    }
  }, [layerRendered, selectedAsset]);

  useEffect(() => {
    if (globalGroupId) {
      hydrateAssetState(previousGlobalGroupIdRef.current);
      if (previousGlobalGroupIdRef.current &&
        previousGlobalGroupIdRef.current !== globalGroupId) {
        removeHighlight(fleetSelectedAssetLayerRef.current);
      }
      previousGlobalGroupIdRef.current = globalGroupId;
      removeAllFeatures(fleetAssetLayerRef.current);
      removeAllFeatures(fleetSelectedAssetLayerRef.current);
      resetObjectIdCache();
      // the layer is emptied, so every asset counts as unsent again and
      // the previous group's queued edits are dropped ...
      appliedAssetsRef.current = new WeakSet<object>();
      pendingAssetsRef.current = [];
    }
  }, [globalGroupId]);

  // current theme: `assets` holds every loaded page, so each new page used
  // to re-send (delete and re-add) all the earlier pages' assets too - only
  // assets not yet on the layer are sent now. The layer is still created
  // from the full list, and an empty list still goes through as before. The
  // legacy theme sends the full list, as before ...
  useEffect(() => {
    const hasAssets   = Array.isArray(assets) && !!assets.length;
    const skipApplied = isCurrentTheme && hasAssets && !!fleetAssetLayerRef.current;
    const data        = skipApplied ? getUnappliedItems(assets, appliedAssetsRef.current) : assets;

    if (!hasAssets || data.length) {
      addGraphicsToLayer({
        layer     : fleetAssetLayerRef.current,
        data,
        create    : createFleetAssetLayers,
        addUpdate : queueAssets
      });
      if (skipApplied) {
        data.forEach(function(asset) {
          if (asset && typeof asset === 'object') {
            appliedAssetsRef.current.add(asset);
          }
        });
      }
    }
  }, [assets]);

  useEffect(() => {
    if (assetUpdates?.length && !isSmoothAssetMovementActiveRef.current) {
      const params = {
        layer     : fleetAssetLayerRef.current,
        data      : assetUpdates,
        create    : createFleetAssetLayers,
        addUpdate : queueAssets
      };
      const delay = setTimeout(() => {
        addGraphicsToLayer(params);
        clearTimeout(delay);
      }, 2000);
    }
  }, [assetUpdates, isSmoothAssetMovementActiveRef.current]);

  useEffect(() => {
    const isActiveTab = document.visibilityState === 'visible';
    if (routeUpdates && isSmoothAssetMovementActiveRef.current) {
      let params: any = {};
      for (const [assetId, {nodes, routes}] of Object.entries(routeUpdates)) {
        if (routes?.length > 2 && isActiveTab) {
          animateAssetPosition(String(assetId), routes);
        } else {
          const endPoint = [nodes[nodes.length - 1]];

          params = {
            layer     : fleetAssetLayerRef.current,
            data      : endPoint,
            create    : createFleetAssetLayers,
            addUpdate : updateAssets
          };

          stopAssetAnimation(String(assetId));
            
          if (!isEmpty(objectIdCacheRef.current.fleet)) {
            delete objectIdCacheRef.current.fleet[assetId];
          }

          if (!isEmpty(objectIdCacheRef.current.selected)) {
            delete objectIdCacheRef.current.selected[assetId];
          }
          addGraphicsToLayer(params);

          if (selectedAssetRef.current?.assetId === Number(assetId)) {
            addUpdateSelectedAsset(endPoint);
            panMapToSelectedAsset(endPoint[0], true);
          }
        }
      }
    }
  }, [routeUpdates]);

  const createFleetAssetLayers = useCallback((assets: CVSAssetType[]) => {
    if (!fleetAssetLayerRef.current && !layerExists(map, FLEET_ASSET_LAYER_ID)) {
      const isEnabled = isCurrentEnabled(activeLayersRef.current);
      createFleetAssetLayer(assets, isEnabled);
      createFleetSelectedAssetLayer(isEnabled);
      setDefinitionExpressionToLayer();
      setLayerHighlighter(fleetSelectedAssetLayerRef.current, view);
      highlightFeature();
    }
  }, [map]);

  const createFleetAssetLayer = useCallback((assets: CVSAssetType[], isEnabled: boolean) => {
    const graphics = createGraphics(assets, FLEET_ASSET_LAYER_FIELDS);
    fleetAssetLayerRef.current = createFeatureLayer(FLEET_ASSET_LAYER_ID, graphics, true);
    map.layers.push(fleetAssetLayerRef.current);
    fleetAssetLayerRef.current?.when(async() => {
      fleetAssetLayerViewRef.current = await view.whenLayerView(fleetAssetLayerRef.current);
      fleetAssetLayerViewRef.current.filter = featureFilter;
      onFleetAssetLayerRendered();
    });
    fleetAssetLayerRef.current.visible = isEnabled;
    setLayerEnabled?.(isEnabled);
    viewClickRef.current = view.on('click', viewClickHandler);
  }, [map, setLayerEnabled]);

  const createFleetSelectedAssetLayer = useCallback((isEnabled: boolean) => {
    const selectedAsset = selectedAssetRef.current ? [selectedAssetRef.current] : [];
    const graphic       = createGraphics(selectedAsset, FLEET_ASSET_LAYER_FIELDS);
    fleetSelectedAssetLayerRef.current = createFeatureLayer(FLEET_SELECTED_ASSET_LAYER_ID, graphic, false);
    map.layers.push(fleetSelectedAssetLayerRef.current);
    fleetSelectedAssetLayerRef.current.visible = isEnabled;
  }, [map]);

  const updateAssets = useCallback(assets => {
    if (fleetAssetLayerRef.current?.visible) {
      const graphics = createGraphics(assets, FLEET_ASSET_LAYER_FIELDS);
      const query    = fleetAssetLayerRef.current?.createQuery();
      query.where    = `assetId IN (${assets?.map(asset => asset?.assetId)})`;
      fleetAssetLayerRef.current?.queryFeatures(query).then(({features}) => {
        applyEditsToLayer(
          fleetAssetLayerRef.current,
          {
            deleteFeatures : features,
            addFeatures    : graphics
          },
          onFindNearbyRefresh
        );
      });
    }
  }, [applyEditsToLayer, onFindNearbyRefresh]);

  // current theme: applies everything queued since the last flush - the
  // debounce keeps only its last call's assets, which would drop a page or an
  // update that arrived within the same 100ms. The legacy theme passes its
  // assets straight in, as before ...
  const AddUpdateAssets = useCallback(debounce((latestAssets?: CVSAssetType[]) => {
    const assets             = latestAssets ?? pendingAssetsRef.current;
    pendingAssetsRef.current = [];
    if (!isEmpty(objectIdCacheRef.current.fleet)) {
      resetObjectIdCache();
    }
    updateAssets(assets);
  }, 100), [applyEditsToLayer, onFindNearbyRefresh]);

  const queueAssets = useCallback((assets: CVSAssetType[]) => {
    if (isCurrentTheme) {
      pendingAssetsRef.current = queueLayerItems(pendingAssetsRef.current, assets);
      AddUpdateAssets();
    } else {
      AddUpdateAssets(assets);
    }
  }, [AddUpdateAssets, isCurrentTheme]);

  const addUpdateSelectedAsset = useCallback((assets: CVSAssetType[]) => {
    if (fleetSelectedAssetLayerRef.current) {
      const graphics = createGraphics(assets, FLEET_ASSET_LAYER_FIELDS);
      fleetSelectedAssetLayerRef.current?.queryFeatures(ALL_FEATURES_DEF).then(({features}) => {
        if (features?.length && !isEmpty(objectIdCacheRef.current.selected)) {
          delete objectIdCacheRef.current.selected[features[0].attributes.assetId];
        }

        applyEditsToLayer(
          fleetSelectedAssetLayerRef.current,
          {
            deleteFeatures : features,
            addFeatures    : graphics
          },
          highlightFeature
        );
      });
    }
  }, [applyEditsToLayer, highlightFeature]);

  const removeAllFeatures = useCallback(async(featureLayer: __esri.FeatureLayer) => {
    await featureLayer?.queryFeatures(ALL_FEATURES_DEF as any)
      .then(({features}) => {
        applyEditsToLayer(
          featureLayer,
          {
            deleteFeatures : features
          }
        );
      });
  }, [applyEditsToLayer]);

  const getAllFeatureAttributes = useCallback(async() => {
    const features = await fleetAssetLayerRef.current?.queryFeatures(ALL_FEATURES_DEF)
      .then(({features}) => features);
    return features?.map(feature => feature?.attributes);
  }, []);

  const viewClickHandler = useCallback(debounce(({x, y}) => {
    if (isCurrentEnabled(activeLayersRef.current)) {
      viewHitTest({x, y}, {
        options     : {include: [fleetAssetLayerRef.current, fleetSelectedAssetLayerRef.current]},
        handler     : onShowPopup,
        zoomHandler : onZoomToClusterHandler,
        layerView   : fleetAssetLayerViewRef.current,
        view
      });
    }
  }, 200), [view, onShowPopup, onZoomToClusterHandler]);

  return null;
};

export default forwardRef(FleetAssetLayer);
